// 归因字段(afSource/afCampaign)展示层格式化，充值订单查询(apple/android)、会员订阅查询、用户管理共用。
// 后端 golang/Java 及日报归因口径不受影响，仅后台展示。

// 已知归因渠道 → 可读名（与 09:30 分端日报的 channel() 口径一致）；未收录的原样透传，不隐藏。
// 取值来自 user_info.af_source 全量实测(2026-09-20)，长尾 *_int 未确认真名故不改写。
export const AF_SOURCE_LABELS: Record<string, string> = {
  // Google Ads(UAC)。googleadwords_int 是旧回传名，2026-06 起 AppsFlyer 改回传 google
  google: 'Google Ads',
  googleadwords_int: 'Google Ads',
  // Apple Ads(ASA)。Apple Search Ads 是 2026-05 前的旧回传名，其 campaign 回传名字而非 ID
  Apple: 'Apple Ads',
  'Apple Search Ads': 'Apple Ads',
  // Meta(Facebook/Instagram/Audience Network)。af_source 的上游是 GA4，2026-09-22 实测 GA4 90 天确定:
  // 广告/手动标记 = 素 token（apps.facebook.com / apps.instagram.com 是 Meta Install Referrer 的
  // utm_source，仅 Android 点击安装；ig / fb4a；'Facebook Ads' 是 AppsFlyer 时代的旧回传名），
  // 社交流入 = 域名（m.facebook.com 等 FB/IG 应用内链接）。标签与 09:30 日报 channel() 逐字一致。
  // 🔴 iOS 没有 Install Referrer，Meta 广告来的 iOS 用户会落成 (direct)，库侧数不出来。
  'apps.facebook.com': 'Meta Ads·Facebook',
  fb4a: 'Meta Ads·Facebook',
  facebook: 'Meta Ads·Facebook',
  fb: 'Meta Ads·Facebook',
  meta: 'Meta Ads·Facebook',
  'Facebook Ads': 'Meta Ads·Facebook', // 2026-04-12 后断投，复投会自动出现
  'apps.instagram.com': 'Meta Ads·Instagram',
  instagram: 'Meta Ads·Instagram',
  ig: 'Meta Ads·Instagram',
  an: 'Meta Ads·Audience Network',
  audiencenetwork: 'Meta Ads·Audience Network',
  'facebook.com': 'Meta 社交·Facebook',
  'm.facebook.com': 'Meta 社交·Facebook',
  'l.facebook.com': 'Meta 社交·Facebook',
  'lm.facebook.com': 'Meta 社交·Facebook',
  'fb.me': 'Meta 社交·Facebook',
  'instagram.com': 'Meta 社交·Instagram',
  'l.instagram.com': 'Meta 社交·Instagram',
  'threads.net': 'Meta 社交·Threads',
  messenger: 'Meta 社交·Messenger',
  'm.me': 'Meta 社交·Messenger',
  // ChatGPT(OpenAI Ads)。2026-10-21 起安卓系列的 Play 链接带 referrer=utm_source=chatgpt&utm_medium=cpc，
  // 只有安卓能带(iOS 直跳 App Store 落 (direct))；chatgpt.com 是 GA4 的 AI 助手引荐(medium=ai-assistant)=自然量。
  chatgpt: 'ChatGPT 广告·安卓',
  'chatgpt.com': 'ChatGPT 自然量',
  tiktokglobal_int: 'TikTok Ads',
  'google-play': 'Play 自然量',
  web: '网页徽章',
  // 2026-10-21 补齐(参照 09:30 日报 channel()；日报 Direct 另按端拆、restricted 原样)：
  // 旧 Meta 社交回传名 / iOS 拒绝 ATT 授权 / GA4 的直接访问
  Social_instagram: 'Meta 社交·Instagram',
  restricted: 'ATT 拒绝(无归因)',
  '(direct)': 'Direct(无归因)', // iOS 没有 Install Referrer，Google/Meta/ChatGPT 的 iOS 广告用户多落在这里
};

// Apple Ads(ASA) campaign ID → 广告系列名。Apple 回传的 afCampaign 是纯数字 ID，
// Google Ads 回传的是名字字符串，故仅对收录的 ID 做替换，其余原样透传。
// 来源：app-ads.apple.com/cm/app/8718180/report（2026-09-22 后台全量 24 条，含已暂停的旧投放；
// 新建 campaign 需在此登记，并同步 ~/platform_report_server.py 与 ~/platform_cohort_report.py 的 ASA）
export const ASA_CAMPAIGN_LABELS: Record<string, string> = {
  '2143009637': '发现-广泛关键词',
  '2143010884': '竞品-精准关键词',
  '2143034586': '精准关键词-通投',
  '2143043637': '测试-观察词',
  '2143066332': '精选特词',
  '2143090083': '2次优化词',
  '2143105134': '4词消耗比',
  '2143154434': '2026年新词',
  '2143269883': '筛选投放',
  '2143567071': 'SUPPART_Brand_JP',
  '2143567709': 'SUPPART_Core_JP',
  '2143569206': 'SUPPART_Discovery_JP',
  '2143571024': 'SUPPART_Competitor_JP',
  '2144463309': 'AUTO_Verified',
  '2144464446': 'AUTO_Longtail',
  '2144464697': 'AUTO_Harvest',
  '2144465036': 'AUTO_Expansion',
  '2144468735': 'TAB_JP_SearchTab',
  '2144471082': 'PP_JP_Browse',
  '2144480450': 'OS_SG_Search',
  '2144480836': 'OS_KSA_Search',
  '2144606006': 'TODAY_JP_Front',
  '2144655636': 'AUTO_Oshi_JP',
  '2144655792': 'AUTO_Story_JP',
};

// 空值 + 字符串 'None' + GA4 括号占位符(如 (none)/(not set)) 统一显示 '-'
const isBlank = (value?: string) => !value || value === 'None' || /^\(.*\)$/.test(value);

// 渠道来源(afSource)格式化：已知渠道映射可读名；其余原样透传
export const formatAttribution = (value?: string) => {
  // 先查映射：'(direct)' 是有意义的取值，不能被下面的括号占位符规则吞成 '-'
  // (typeof 判断：toString 之类的取值会命中原型上的函数)
  const known = value ? AF_SOURCE_LABELS[value.trim()] : undefined;
  if (typeof known === 'string') return known;
  if (isBlank(value)) return '-';
  const v = value!.trim();
  // GA4 的 source 大小写不统一(Apple / facebook.com)，故原样查不到时再按小写查一次
  return AF_SOURCE_LABELS[v] ?? AF_SOURCE_LABELS[v.toLowerCase()] ?? v;
};

// 注册端(user_info.device_type)：1/2 = App 注册；3/4/5 = 网页注册(H5/PC)，前端写 getDeviceType()
const WEB_CHANNEL_LABELS: Record<number, string> = {
  3: 'H5·iOS浏览器',
  4: 'H5·安卓浏览器',
  5: 'PC Web',
};

// 注册来源：iOS / Android / Web
export const formatRegisterPlatform = (deviceType?: number) => {
  if (deviceType === 1) return 'iOS';
  if (deviceType === 2) return 'Android';
  if (deviceType && WEB_CHANNEL_LABELS[deviceType]) return 'Web';
  return '-';
};

// 渠道来源：网页注册的用户 afSource 基本为空(GA4/AppsFlyer 只回传 App)，标出 H5/PC Web 渠道；
// 有 afSource 时拼在后面，App 注册的用户与原 formatAttribution 一致
export const formatChannel = (afSource?: string, deviceType?: number) => {
  const attribution = formatAttribution(afSource);
  const web = deviceType ? WEB_CHANNEL_LABELS[deviceType] : undefined;
  if (!web) return attribution;
  return attribution === '-' ? web : `${web} / ${attribution}`;
};

// 归因数据(afCampaign)格式化：ASA 的数字 campaign ID 换成广告系列名；其余原样透传
export const formatCampaign = (value?: string) => {
  if (isBlank(value)) return '-';
  return ASA_CAMPAIGN_LABELS[value!] ?? value!;
};
